import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { MultiMediaFileSelector } from '../components/media/MultiMediaFileSelector';
import { MediaFile } from '../services/mediaService';
import { useClient } from '../contexts/ClientContext';
import { useToast } from '../hooks/use-toast';
import { getApiUrl } from '../utils/config';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { TestTube, Image, Sparkles } from 'lucide-react';

const MultiImageTestPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentClient } = useClient();
  const { toast } = useToast();
  
  const [primaryImage, setPrimaryImage] = useState<MediaFile[]>([]);
  const [secondaryImage, setSecondaryImage] = useState<MediaFile[]>([]);
  const [prompt, setPrompt] = useState('Create a professional advertisement combining these images');
  const [placement, setPlacement] = useState('bottom-right corner');
  const [isLoading, setIsLoading] = useState(false);
  const [taskId, setTaskId] = useState<string | null>(null);

  const handleTest = async () => {
    if (!currentClient) {
      toast({ title: "Error", description: "No client selected", variant: "destructive" });
      return;
    }

    if (primaryImage.length === 0 || secondaryImage.length === 0) {
      toast({ title: "Error", description: "Please select both images", variant: "destructive" });
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await fetch(`${getApiUrl()}/api/v1/ai/test-multi-image-simple`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
          'X-Client-ID': currentClient.id,
        },
        body: JSON.stringify({
          primary_image_id: primaryImage[0].id,
          secondary_image_id: secondaryImage[0].id,
          prompt: prompt,
          placement_instruction: placement
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to start multi-image test');
      }

      const result = await response.json();
      
      if (result.success && result.generated_image_url) {
        setTaskId(result.generated_image_url); // Reuse taskId state to store the image URL
        toast({ 
          title: "Test Successful!", 
          description: "Multi-image generation completed successfully!",
          duration: 5000
        });
      } else {
        toast({ 
          title: "Test Failed", 
          description: result.message || "Multi-image generation failed",
          variant: "destructive"
        });
      }
      
    } catch (error) {
      console.error('Multi-image test error:', error);
      toast({ 
        title: "Test Failed", 
        description: error instanceof Error ? error.message : "Unknown error occurred", 
        variant: "destructive" 
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-900 dark:via-slate-900 dark:to-gray-900">
      {/* Header */}
      <div className="backdrop-blur-2xl bg-white/60 dark:bg-slate-900/60 border-b border-slate-200/50 dark:border-slate-700/30">
        <div className="px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 via-pink-500 to-red-500 rounded-2xl flex items-center justify-center shadow-lg shadow-purple-500/25">
                <TestTube className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-primary via-purple-600 to-pink-600 bg-clip-text text-transparent">
                  Multi-Image Test
                </h1>
                <p className="hidden sm:block text-muted-foreground">Test Gemini's multi-image capabilities</p>
              </div>
            </div>
            <Button variant="outline" onClick={() => navigate('/app/tasks')}>
              View Tasks
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-8 py-8">
        <div className="max-w-4xl mx-auto">
          <Card className="bg-white/90 dark:bg-slate-800/50 backdrop-blur-xl border border-slate-200/60 dark:border-slate-600/40 shadow-xl">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Sparkles className="w-5 h-5 text-purple-500" />
                <span>Multi-Image Generation Test</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              
              {/* Primary Image Selection */}
              <div>
                <Label className="text-sm font-semibold mb-3 block">Primary Image (Product/Main Content)</Label>
                <MultiMediaFileSelector
                  selectedFiles={primaryImage}
                  onSelectionChange={setPrimaryImage}
                  maxFiles={1}
                  acceptedTypes={['image']}
                />
              </div>

              {/* Secondary Image Selection */}
              <div>
                <Label className="text-sm font-semibold mb-3 block">Secondary Image (Logo/Overlay)</Label>
                <MultiMediaFileSelector
                  selectedFiles={secondaryImage}
                  onSelectionChange={setSecondaryImage}
                  maxFiles={1}
                  acceptedTypes={['image']}
                />
              </div>

              {/* Prompt Input */}
              <div>
                <Label className="text-sm font-semibold mb-3 block">Generation Prompt</Label>
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 bg-background/50 border border-slate-200 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
                  placeholder="Describe how you want the images combined..."
                />
              </div>

              {/* Placement Instruction */}
              <div>
                <Label className="text-sm font-semibold mb-3 block">Logo Placement</Label>
                <select
                  value={placement}
                  onChange={(e) => setPlacement(e.target.value)}
                  className="w-full px-3 py-2 bg-background/50 border border-slate-200 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                >
                  <option value="bottom-right corner">Bottom Right Corner</option>
                  <option value="bottom-left corner">Bottom Left Corner</option>
                  <option value="top-right corner">Top Right Corner</option>
                  <option value="top-left corner">Top Left Corner</option>
                  <option value="center">Center</option>
                  <option value="bottom center">Bottom Center</option>
                  <option value="top center">Top Center</option>
                </select>
              </div>

              {/* Test Button */}
              <div className="pt-4">
                <Button
                  onClick={handleTest}
                  disabled={isLoading || primaryImage.length === 0 || secondaryImage.length === 0}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Running Test...
                    </>
                  ) : (
                    <>
                      <TestTube className="w-4 h-4 mr-2" />
                      Start Multi-Image Test
                    </>
                  )}
                </Button>
              </div>

              {/* Result Display */}
              {taskId && (
                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                  <p className="text-sm text-green-800 dark:text-green-200 mb-3">
                    <strong>Test Successful!</strong> Multi-image generation completed.
                  </p>
                  <div className="bg-white dark:bg-slate-800 rounded-lg p-2 border">
                    <img 
                      src={taskId} 
                      alt="Generated multi-image result" 
                      className="w-full max-w-md mx-auto rounded-lg shadow-lg"
                      onError={(e) => {
                        console.error('Image load error:', e);
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                  <p className="text-xs text-green-600 dark:text-green-400 mt-2">
                    ✅ Gemini successfully combined both images!
                  </p>
                </div>
              )}

            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default MultiImageTestPage;